import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, useAuth } from "../App";
import { toast } from "sonner";
import {
  ArrowLeft, Copy, ExternalLink, Trash2, Star, Plus, X, Link2,
  FileText, Layers, Play,
} from "lucide-react";
import { inferContent } from "../lib/contentKind";
import { openAttachmentUrl } from "../lib/openAttachment";
import AvatarChip from "../components/ui/avatar-chip";

/**
 * Shareable detail page for a Library item. Mounted at /library/:deckId.
 *
 * Visually identical to the old DetailModal — same dark full-bleed shell,
 * same hero + attachments block — but lives on a real route so the URL
 * can be copy-pasted into Slack/email and open directly to this item.
 *
 * Side-effects on mount:
 *   - Fetches the item via GET /pitch-decks/:deckId (auth-gated, returns
 *     starred_by_me + star_count derived fields).
 *   - Fires POST /pitch-decks/:deckId/view (fire-and-forget) to accrue
 *     view counts + event_log entries for Phase-2 trending surfaces.
 */
export default function LibraryDetailPage() {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const uid = user?.user_id || user?.id;
  const isAdmin = (user?.role || "").toLowerCase() === "admin";

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeAttIdx, setActiveAttIdx] = useState(null);
  const [iframeErrored, setIframeErrored] = useState(false);
  const [editingTags, setEditingTags] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  // Full library list — used to compute "Related lessons" in the rail. Fetched
  // once on mount, cheap at current scale (<500 items). We ignore the response
  // if the request errors — Related is a nice-to-have, not critical.
  const [allItems, setAllItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/pitch-decks/${deckId}`);
        if (cancelled) return;
        setItem(res.data);
        setNotFound(false);
      } catch (e) {
        if (e.response?.status === 404) setNotFound(true);
        else toast.error("Couldn't load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    // Fire-and-forget view pulse — silent on failure.
    api.post(`/pitch-decks/${deckId}/view`).catch(() => {});
    // Fetch library list for Related lessons in parallel; fail silently.
    api.get("/pitch-decks").then((r) => { if (!cancelled) setAllItems(r.data || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, [deckId]);

  useEffect(() => { setIframeErrored(false); }, [activeAttIdx, deckId]);

  // Close on Escape — standard page-level shortcut. Navigates back to Library.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") navigate("/pitch-decks");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  const attachments = item?.attachments || [];
  const active = activeAttIdx != null ? attachments[activeAttIdx] : null;
  const activeUrl = active
    ? active.content_url
    : (item?.content_url || item?.canva_url || "");
  const inf = useMemo(() => inferContent(activeUrl), [activeUrl]);
  const embedUrl = inf.embedUrl;
  const activeTitle = active ? (active.label || active.filename || inf.label) : (item?.title || "");

  // Related lessons — other items scored by tag overlap with the current
  // item. Hoisted above the loading/notFound early returns so the hook
  // order stays stable between renders.
  const related = useMemo(() => {
    if (!item || !allItems?.length) return [];
    const myTags = new Set((item.tags || []).map((t) => (t || "").toLowerCase()));
    const pool = allItems.filter((x) => x.deck_id !== item.deck_id);
    const score = (x) => {
      const xs = new Set((x.tags || []).map((t) => (t || "").toLowerCase()));
      let tagOverlap = 0;
      for (const t of xs) if (myTags.has(t)) tagOverlap += 1;
      const uploaderMatch = x.added_by === item.added_by ? 1 : 0;
      const kindMatch = x.kind === item.kind ? 0.5 : 0;
      return tagOverlap * 10 + uploaderMatch * 2 + kindMatch;
    };
    return pool
      .map((x) => ({ x, s: score(x) }))
      .sort((a, b) => b.s - a.s || String(b.x.created_at || "").localeCompare(String(a.x.created_at || "")))
      .slice(0, 5)
      .map(({ x }) => x);
  }, [item, allItems]);

  const canDelete = !!item && (isAdmin || item.added_by === uid);
  const canEdit = canDelete;

  const goBack = () => {
    // Prefer real history back so the grid's scroll + filter state is restored.
    // Fallback to /pitch-decks if there's nothing to go back to (direct link).
    if (window.history.length > 1) navigate(-1);
    else navigate("/pitch-decks");
  };

  const copyLink = () => {
    // Absolute, shareable URL — not the attachment content_url.
    const shareUrl = `${window.location.origin}/library/${deckId}`;
    navigator.clipboard?.writeText(shareUrl);
    toast.success("Link copied");
  };

  const toggleStar = async () => {
    if (!item) return;
    const prev = item;
    // Optimistic flip
    setItem({
      ...item,
      starred_by_me: !item.starred_by_me,
      star_count: (item.star_count || 0) + (item.starred_by_me ? -1 : 1),
    });
    try {
      await api.post(`/pitch-decks/${deckId}/star`);
    } catch (e) {
      setItem(prev);
      toast.error("Couldn't update star");
    }
  };

  // Explicit "Mark as watched" toggle. Distinct from the passive view pulse
  // that fires on mount — this is the user saying "I actually watched this
  // end-to-end" and wanting it out of their Unwatched filter.
  const toggleCompleted = async () => {
    if (!item) return;
    const prev = item;
    const meId = uid;
    const meName = user?.full_name || user?.name || "";
    const now = new Date().toISOString();
    const wasCompleted = !!item.completed_by_me;
    // Optimistic update: flip the derived flag + count, and add/remove our own
    // row in the completers array so the "Watched by" strip updates instantly.
    setItem({
      ...item,
      completed_by_me: !wasCompleted,
      completer_count: Math.max(0, (item.completer_count || 0) + (wasCompleted ? -1 : 1)),
      completers: wasCompleted
        ? (item.completers || []).filter((c) => c.user_id !== meId)
        : [{ user_id: meId, name: meName, at: now }, ...(item.completers || []).filter((c) => c.user_id !== meId)],
    });
    try {
      await api.post(`/pitch-decks/${deckId}/complete`);
      if (!wasCompleted) toast.success("Marked as watched");
    } catch (e) {
      setItem(prev);
      toast.error("Couldn't update");
    }
  };

  const handleDelete = () => {
    if (!window.confirm(`Remove "${item.title}"? This can't be undone from here.`)) return;
    api.delete(`/pitch-decks/${deckId}`)
      .then(() => { toast.success("Removed"); navigate("/pitch-decks"); })
      .catch((e) => toast.error(e.response?.data?.detail || "Couldn't remove"));
  };

  const addAttachment = async (payload) => {
    try {
      const res = await api.post(`/pitch-decks/${deckId}/attachments`, payload);
      setItem((prev) => prev ? { ...prev, attachments: res.data.attachments } : prev);
      toast.success("Added");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't add"); }
  };

  const removeAttachment = async (idx) => {
    if (!window.confirm("Remove this material?")) return;
    try {
      const res = await api.delete(`/pitch-decks/${deckId}/attachments/${idx}`);
      setItem((prev) => prev ? { ...prev, attachments: res.data.attachments } : prev);
      if (activeAttIdx === idx) setActiveAttIdx(null);
      else if (activeAttIdx != null && activeAttIdx > idx) setActiveAttIdx(activeAttIdx - 1);
      toast.success("Removed");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't remove"); }
  };

  const saveTags = async (newTags) => {
    try {
      const res = await api.post(`/pitch-decks/${deckId}/tags`, { tags: newTags });
      setItem((prev) => prev ? { ...prev, tags: res.data.tags } : prev);
      setEditingTags(false);
      toast.success("Tags updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save tags"); }
  };

  // Dark immersive shell — NOT position:fixed because the page sits inside
  // the app's content column (which is already offset by the sidebar). Old
  // `position:fixed; inset:0` covered the whole viewport including the 214px
  // sidebar, so the first few characters of every line were hidden under it.
  // As a normal block it naturally starts at the right of the sidebar and
  // scrolls with the page — correct behaviour for a shareable route, not a
  // modal.
  const shellStyle = {
    background: "rgba(0,0,0,.9)",
    minHeight: "100vh",
    padding: "40px 24px",
    animation: "fadeIn .25s ease-out",
    fontFamily: "var(--font-sans)",
  };

  if (loading) {
    return (
      <div style={shellStyle}>
        <div style={{ maxWidth: 1080, margin: "0 auto", color: "rgba(255,255,255,.5)", textAlign: "center", padding: "80px 20px" }}>
          Loading…
        </div>
      </div>
    );
  }

  if (notFound || !item) {
    return (
      <div style={shellStyle}>
        <div style={{ maxWidth: 640, margin: "60px auto 0", textAlign: "center", color: "rgba(255,255,255,.7)" }}>
          <Link2 size={36} strokeWidth={1.5} style={{ opacity: .4, marginBottom: 16 }} />
          <h2 style={{ color: "white", fontSize: 22, fontWeight: 600, margin: 0 }}>Item not found</h2>
          <p style={{ marginTop: 10, fontSize: 14 }}>
            It may have been removed, or the link is out of date.
          </p>
          <button
            onClick={() => navigate("/pitch-decks")}
            style={{ marginTop: 20, padding: "10px 22px", background: "white", color: "black", border: "none", borderRadius: 999, fontSize: 14, fontWeight: 600, cursor: "pointer" }}
          >
            Back to Library
          </button>
        </div>
      </div>
    );
  }

  const hasAttachmentsRail = attachments.length > 0 || canEdit;

  // Truncate description to 2 lines by default (YouTube-style "…more" toggle).
  // Anything short enough to fit is shown without the toggle.
  const descLongEnoughToTruncate = (item.description || "").length > 180;

  return (
    <div style={shellStyle}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        {/* Top bar — minimal now: just Back. All action buttons moved below
            the hero so the detail page feels like YouTube, not a modal with
            a crowded chrome strip. */}
        <div className="dm-lib-detail-topbar" style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <button
            onClick={goBack}
            title="Back to Library (Esc)"
            style={pillBtn}
          >
            <ArrowLeft size={14} /> Back
          </button>
        </div>

        {/* Two-column body, YouTube shape: video + metadata below on the left,
            "Up next" / supporting materials rail on the right.
            Mobile (< 768px) collapses to a single column — see App.css. */}
        <div
          className="dm-lib-detail-body"
          style={{
            display: "grid",
            gridTemplateColumns: (hasAttachmentsRail || related.length > 0) ? "minmax(0, 1fr) 340px" : "1fr",
            gap: 32,
            alignItems: "start",
          }}
        >
        <div className="dm-lib-detail-main" style={{ minWidth: 0 }}>
        {/* Hero — embed or fallback */}
        <div style={{
          width: "100%",
          aspectRatio: inf.kind === "doc" ? "4 / 5" : "16 / 9",
          maxHeight: "70vh",
          background: "#000",
          border: "1px solid rgba(255,255,255,.08)",
          borderRadius: 14,
          overflow: "hidden",
          marginBottom: 20,
          position: "relative",
        }}>
          {embedUrl && !iframeErrored ? (
            <iframe
              key={activeUrl}
              src={embedUrl}
              title={activeTitle}
              style={{ width: "100%", height: "100%", border: "none" }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              onError={() => setIframeErrored(true)}
            />
          ) : (
            <div style={{
              width: "100%", height: "100%",
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              gap: 16, color: "rgba(255,255,255,.6)", textAlign: "center", padding: 40,
            }}>
              <Link2 size={36} strokeWidth={1.5} />
              <div style={{ fontSize: 15, fontWeight: 500 }}>
                {inf.kind === "deck" ? "Preview isn't available — the source may not be public" : "This one opens externally"}
              </div>
              <a
                href={activeUrl} target="_blank" rel="noopener noreferrer"
                style={{ marginTop: 4, padding: "10px 22px", background: "white", color: "black", textDecoration: "none", borderRadius: 999, fontSize: 14, fontWeight: 600 }}
              >
                Open in new tab
              </a>
            </div>
          )}
          {active && (
            <div style={{
              position: "absolute", top: 14, left: 14,
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "7px 12px",
              background: "rgba(0,0,0,.6)",
              backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
              borderRadius: 999, color: "white", fontSize: 12, fontWeight: 500,
            }}>
              <span style={{ opacity: .7 }}>Now viewing:</span>
              <span>{activeTitle}</span>
              <button
                onClick={() => setActiveAttIdx(null)}
                style={{ background: "rgba(255,255,255,.15)", border: "none", borderRadius: 999, padding: "3px 10px", color: "white", fontSize: 11, fontWeight: 600, cursor: "pointer", marginLeft: 4 }}
              >
                Back to main
              </button>
            </div>
          )}
        </div>

        {/* Title — larger than before, YouTube-sized */}
        <h1 style={{ fontSize: 22, fontWeight: 600, color: "white", letterSpacing: "-0.015em", margin: 0, lineHeight: 1.3 }}>
          {item.title}
        </h1>

        {/* Meta row: views + watched count + date + kind. Matches YouTube's
            "1.2K views · 3 days ago" format. */}
        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "rgba(255,255,255,.55)", flexWrap: "wrap" }}>
          {item.viewer_count > 0 && (
            <>
              <span>{item.viewer_count} {item.viewer_count === 1 ? "view" : "views"}</span>
              <span style={{ color: "rgba(255,255,255,.3)" }}>·</span>
            </>
          )}
          {item.completer_count > 0 && (
            <>
              <span>{item.completer_count} watched</span>
              <span style={{ color: "rgba(255,255,255,.3)" }}>·</span>
            </>
          )}
          <span>{new Date(item.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span>
          <span style={{ color: "rgba(255,255,255,.3)" }}>·</span>
          <span>{inferContent(item.content_url || item.canva_url || "").label}</span>
        </div>

        {/* Uploader strip + action-pill row — YouTube-style: avatar/name on
            the left, actions on the right. Lays out as a flex row that wraps
            on narrow screens. */}
        <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {item.added_by_name && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, paddingRight: 12, borderRight: "1px solid rgba(255,255,255,.1)" }}>
              <AvatarChip name={item.added_by_name} size={32} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, color: "white" }}>{item.added_by_name}</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,.45)" }}>Uploader</div>
              </div>
            </div>
          )}
          <div style={{ flex: 1 }} />
          <button
            onClick={toggleCompleted}
            title={item.completed_by_me ? "Unmark as watched" : "Mark as watched"}
            style={{
              ...pillBtn,
              color: item.completed_by_me ? "#22c55e" : "rgba(255,255,255,.85)",
              borderColor: item.completed_by_me ? "rgba(34,197,94,.4)" : "rgba(255,255,255,.12)",
              background: item.completed_by_me ? "rgba(34,197,94,.08)" : "rgba(255,255,255,.06)",
              fontWeight: 600,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" {...(!item.completed_by_me ? { strokeOpacity: ".5" } : {})}/>
            </svg>
            {item.completed_by_me ? "Watched" : "Mark as watched"}
          </button>
          <button
            onClick={toggleStar}
            title={item.starred_by_me ? "Unstar" : "Star this item"}
            style={{
              ...pillBtn,
              color: item.starred_by_me ? "#fbbf24" : "rgba(255,255,255,.85)",
              borderColor: item.starred_by_me ? "rgba(251,191,36,.4)" : "rgba(255,255,255,.12)",
              background: item.starred_by_me ? "rgba(251,191,36,.08)" : "rgba(255,255,255,.06)",
              fontWeight: 600,
            }}
          >
            <Star size={13} fill={item.starred_by_me ? "#fbbf24" : "none"} />
            {item.star_count > 0 ? item.star_count : "Star"}
          </button>
          <button
            onClick={copyLink}
            title="Copy shareable link"
            style={{ ...pillBtn, background: "rgba(255,255,255,.06)" }}
          >
            <Copy size={13} /> Share
          </button>
          <a
            href={activeUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...pillBtn, textDecoration: "none", background: "rgba(255,255,255,.06)" }}
          >
            <ExternalLink size={13} /> Open
          </a>
          {canDelete && (
            <button
              onClick={handleDelete}
              title="Remove from Library"
              style={{
                ...pillBtn,
                color: "#ef4444",
                borderColor: "rgba(239,68,68,.3)",
                background: "transparent",
              }}
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>

        {/* Description panel — YouTube-style. Truncated to 2 lines by default
            (or fully shown if short), click to expand. Gray rounded card so
            the click target is obvious. */}
        {item.description && (
          <div
            onClick={() => descLongEnoughToTruncate && setDescExpanded(!descExpanded)}
            style={{
              marginTop: 14,
              padding: "12px 14px",
              background: "rgba(255,255,255,.04)",
              border: "1px solid rgba(255,255,255,.06)",
              borderRadius: 10,
              cursor: descLongEnoughToTruncate ? "pointer" : "default",
              transition: "background .15s",
            }}
            onMouseEnter={(e) => { if (descLongEnoughToTruncate) e.currentTarget.style.background = "rgba(255,255,255,.06)"; }}
            onMouseLeave={(e) => { if (descLongEnoughToTruncate) e.currentTarget.style.background = "rgba(255,255,255,.04)"; }}
          >
            <div
              style={{
                color: "rgba(255,255,255,.85)",
                fontSize: 14,
                lineHeight: 1.55,
                display: descLongEnoughToTruncate && !descExpanded ? "-webkit-box" : "block",
                WebkitLineClamp: descLongEnoughToTruncate && !descExpanded ? 2 : "unset",
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                whiteSpace: "pre-wrap",
              }}
            >
              {item.description}
            </div>
            {descLongEnoughToTruncate && (
              <div style={{ marginTop: 6, fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,.6)" }}>
                {descExpanded ? "Show less" : "…more"}
              </div>
            )}
          </div>
        )}

        {/* Tags — below description, YouTube-ish "topics" */}
        <div style={{ marginTop: 14 }}>
          <TagsRow
            tags={item.tags || []}
            canEdit={canEdit}
            editing={editingTags}
            onStartEdit={() => setEditingTags(true)}
            onCancelEdit={() => setEditingTags(false)}
            onSave={saveTags}
            onTagClick={(tag) => navigate(`/pitch-decks?tag=${encodeURIComponent(tag)}`)}
          />
        </div>

        {/* Watched-by row — below tags. Avatar stack of explicit completers,
            with a softer secondary count for passive openers. */}
        {(item.completers || []).length > 0 && (
          <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "rgba(255,255,255,.55)", fontWeight: 500 }}>
              Watched by
            </span>
            <div style={{ display: "flex", alignItems: "center" }}>
              {item.completers.slice(0, 6).map((c, i) => (
                <div
                  key={c.user_id || i}
                  title={`${c.name || "?"}${c.at ? " · " + new Date(c.at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : ""}`}
                  style={{
                    marginLeft: i === 0 ? 0 : -8,
                    border: "2px solid #0b0b0d",
                    borderRadius: "50%",
                    zIndex: 10 - i,
                  }}
                >
                  <AvatarChip name={c.name || "?"} size={24} />
                </div>
              ))}
            </div>
            {item.completer_count > 6 && (
              <span style={{ fontSize: 12, color: "rgba(255,255,255,.5)" }}>
                +{item.completer_count - 6} more
              </span>
            )}
            {item.viewer_count > 0 && item.viewer_count !== item.completer_count && (
              <span style={{ fontSize: 12, color: "rgba(255,255,255,.4)", marginLeft: 4 }}>
                · {item.viewer_count} opened
              </span>
            )}
          </div>
        )}
        {item.completers && item.completers.length === 0 && item.viewer_count > 0 && (
          <div style={{ marginTop: 20, fontSize: 12, color: "rgba(255,255,255,.4)" }}>
            {item.viewer_count} {item.viewer_count === 1 ? "person has" : "people have"} opened this · nobody's marked it watched yet
          </div>
        )}

        </div>

        {/* Side rail: supporting materials + Related lessons (YouTube "Up
            next"-style). Not sticky any more — users scroll through the
            description and the rail scrolls naturally with it, same as
            YouTube. Sticky was creating a fixed-position feel that fought
            the natural flow. */}
        {(hasAttachmentsRail || related.length > 0) && (
          <aside
            className="dm-lib-detail-rail"
            style={{ display: "flex", flexDirection: "column", gap: 16 }}
          >
            {hasAttachmentsRail && (
              <div style={{
                background: "rgba(255,255,255,.03)",
                border: "1px solid rgba(255,255,255,.08)",
                borderRadius: 14,
                padding: 16,
              }}>
                <div style={{
                  fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,.7)",
                  letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12,
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                }}>
                  <span>Supporting materials</span>
                  {attachments.length > 0 && (
                    <span style={{ color: "rgba(255,255,255,.4)", fontWeight: 500 }}>{attachments.length}</span>
                  )}
                </div>
                {attachments.length > 0 ? (
                  <div className="dm-lib-att-stack" style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: canEdit ? 12 : 0 }}>
                    {attachments.map((a, i) => (
                      <AttachmentTile
                        key={i}
                        attachment={a}
                        active={activeAttIdx === i}
                        onPlay={() => setActiveAttIdx(i)}
                        onRemove={canEdit ? () => removeAttachment(i) : null}
                      />
                    ))}
                  </div>
                ) : canEdit ? (
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,.45)", marginBottom: 10 }}>
                    Add reading material, extra decks, reference docs — anything that supports this item.
                  </div>
                ) : null}
                {canEdit && <InlineAddAttachment onAdd={addAttachment} />}
              </div>
            )}

            {/* Related lessons — items scored by tag overlap with the current
                lesson. Shown as a vertical stack of thumbnail+title cards
                (YouTube "Up next" shape). Click navigates to that lesson. */}
            {related.length > 0 && (
              <div>
                <div style={{
                  fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,.7)",
                  letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10,
                  paddingLeft: 2,
                }}>
                  Related lessons
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {related.map((r) => (
                    <RelatedItemCard
                      key={r.deck_id}
                      item={r}
                      onOpen={() => navigate(`/library/${r.deck_id}`)}
                    />
                  ))}
                </div>
              </div>
            )}
          </aside>
        )}
        </div>
      </div>
    </div>
  );
}

// Shared styling for the top-bar pill buttons. Single object so every button
// feels identical; per-button overrides extend it via spread.
const pillBtn = {
  padding: "8px 14px",
  background: "transparent",
  border: "1px solid rgba(255,255,255,.1)",
  borderRadius: 999,
  color: "rgba(255,255,255,.8)",
  fontFamily: "var(--font-sans)",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

/* ═══════════════ Tags row — clickable chips + inline editor ═══════════════ */

function TagsRow({ tags, canEdit, editing, onStartEdit, onCancelEdit, onSave, onTagClick }) {
  const [draft, setDraft] = useState("");
  const [localTags, setLocalTags] = useState(tags);
  useEffect(() => { setLocalTags(tags); }, [tags]);

  if (editing) {
    const addDraftTag = () => {
      const v = draft.trim().toLowerCase().replace(/^#/, "").replace(/\s+/g, "-");
      if (!v) return;
      if (localTags.includes(v)) { setDraft(""); return; }
      setLocalTags([...localTags, v]);
      setDraft("");
    };
    return (
      <div style={{ marginTop: 22, padding: 14, background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 12 }}>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)", marginBottom: 8, letterSpacing: "0.08em", textTransform: "uppercase" }}>Tags</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
          {localTags.map((t) => (
            <span key={t} style={chipStyle(true)}>
              #{t}
              <button
                type="button"
                onClick={() => setLocalTags(localTags.filter((x) => x !== t))}
                style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer", padding: 0, marginLeft: 4, display: "flex" }}
              ><X size={11} /></button>
            </span>
          ))}
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addDraftTag(); }
              else if (e.key === "Backspace" && !draft && localTags.length) {
                setLocalTags(localTags.slice(0, -1));
              }
            }}
            placeholder="Add tag + Enter"
            style={{ background: "transparent", border: "none", color: "white", fontSize: 13, outline: "none", minWidth: 140, fontFamily: "var(--font-sans)" }}
          />
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onCancelEdit} style={{ ...pillBtn, padding: "6px 14px", fontSize: 12 }}>Cancel</button>
          <button
            onClick={() => { if (draft.trim()) addDraftTag(); onSave(localTags); }}
            style={{ ...pillBtn, padding: "6px 14px", fontSize: 12, background: "white", color: "black", borderColor: "white" }}
          >Save</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 22, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
      {tags.length === 0 ? (
        canEdit ? (
          <button onClick={onStartEdit} style={{ ...pillBtn, padding: "6px 12px", fontSize: 12, color: "rgba(255,255,255,.5)", borderStyle: "dashed" }}>
            <Plus size={12} /> Add tags
          </button>
        ) : null
      ) : (
        <>
          {tags.map((t) => (
            <button key={t} onClick={() => onTagClick(t)} style={chipStyle(false)} title={`Filter Library by #${t}`}>
              #{t}
            </button>
          ))}
          {canEdit && (
            <button onClick={onStartEdit} style={{ ...pillBtn, padding: "4px 10px", fontSize: 11, color: "rgba(255,255,255,.4)", borderStyle: "dashed" }}>
              Edit
            </button>
          )}
        </>
      )}
    </div>
  );
}

const chipStyle = (filled) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "4px 10px",
  background: filled ? "rgba(96,165,250,.16)" : "rgba(255,255,255,.06)",
  border: `1px solid ${filled ? "rgba(96,165,250,.3)" : "rgba(255,255,255,.1)"}`,
  color: filled ? "#93c5fd" : "rgba(255,255,255,.75)",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
});

/* ═══════════════ Attachment tile ═══════════════ */

function AttachmentTile({ attachment, active, onPlay, onRemove }) {
  const inf = inferContent(attachment.content_url);
  const Icon = inf.kind === "video" ? Play : inf.kind === "deck" ? Layers : FileText;
  const displayLabel = attachment.label || attachment.filename || inf.label;
  const isUploadedFile = (attachment.content_url || "").startsWith("/api/pitch-decks/file/");
  const canEmbed = !!inf.embedUrl && !isUploadedFile;
  const [opening, setOpening] = useState(false);

  const handleClick = async () => {
    if (canEmbed) { onPlay?.(); return; }
    if (opening) return;
    setOpening(true);
    try { await openAttachmentUrl(attachment.content_url, attachment.filename); }
    finally { setOpening(false); }
  };

  return (
    <div
      onClick={handleClick}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "rgba(255,255,255,.08)"; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "rgba(255,255,255,.04)"; }}
      title={canEmbed ? "Click to view here" : "Click to open in new tab"}
      style={{
        background: active ? "rgba(96,165,250,.14)" : "rgba(255,255,255,.04)",
        border: `1px solid ${active ? "rgba(96,165,250,.4)" : "rgba(255,255,255,.08)"}`,
        // Blue left-bar when active — makes it unmistakable which tile is
        // currently loaded in the hero. Left padding ticks up so content
        // doesn't jump when the bar appears.
        borderLeft: active ? "3px solid #60a5fa" : "1px solid rgba(255,255,255,.08)",
        borderRadius: 12, padding: active ? "12px 14px 12px 12px" : "12px 14px",
        display: "flex", alignItems: "center", gap: 12,
        cursor: "pointer", transition: "all .15s", position: "relative",
      }}
    >
      <div style={{
        width: 36, height: 36, borderRadius: 8,
        background: inf.kind === "video" ? "rgba(167,139,250,.14)" : inf.kind === "deck" ? "rgba(96,165,250,.14)" : "rgba(251,191,36,.14)",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: inf.kind === "video" ? "#a78bfa" : inf.kind === "deck" ? "#60a5fa" : "#fbbf24",
        flexShrink: 0,
      }}>
        <Icon size={16} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: "white", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {displayLabel}
        </div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)", marginTop: 2 }}>
          {inf.label}
          {opening ? " · opening…" : active ? " · now viewing" : canEmbed ? "" : " · opens in new tab"}
        </div>
      </div>
      {!canEmbed && (
        <button
          type="button"
          onClick={async (e) => {
            e.stopPropagation();
            if (opening) return;
            setOpening(true);
            try { await openAttachmentUrl(attachment.content_url, attachment.filename); }
            finally { setOpening(false); }
          }}
          disabled={opening}
          title="Open in new tab"
          style={{ background: "transparent", border: "none", color: "rgba(255,255,255,.5)", cursor: opening ? "wait" : "pointer", display: "flex", alignItems: "center", padding: 4 }}
        >
          <ExternalLink size={14} />
        </button>
      )}
      {onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          title="Remove"
          style={{ background: "transparent", border: "none", color: "rgba(255,255,255,.4)", cursor: "pointer", padding: 4, display: "flex" }}
        >
          <Trash2 size={13} />
        </button>
      )}
    </div>
  );
}

/* ═══════════════ Inline add-material block ═══════════════ */

function InlineAddAttachment({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [uploading, setUploading] = useState(false);

  const submit = async () => {
    if (!url.trim()) return;
    await onAdd({ content_url: url.trim(), label: label.trim() || null });
    setUrl(""); setLabel(""); setOpen(false);
  };

  const onFile = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File exceeds 25MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/pitch-decks/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await onAdd({
        content_url: res.data.content_url,
        filename: res.data.filename,
        label: label.trim() || null,
        kind: res.data.kind,
      });
      setLabel(""); setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          padding: "10px 16px", background: "transparent",
          border: "1px dashed rgba(255,255,255,.2)", borderRadius: 999,
          color: "rgba(255,255,255,.7)", fontSize: 13, fontWeight: 500,
          cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
        }}
      >
        <Plus size={14} /> Add material
      </button>
    );
  }

  return (
    <div style={{
      padding: 14, background: "rgba(255,255,255,.04)",
      border: "1px solid rgba(255,255,255,.1)", borderRadius: 12,
      display: "flex", flexDirection: "column", gap: 10,
    }}>
      <input
        type="url" autoFocus value={url} onChange={(e) => setUrl(e.target.value)}
        placeholder="Paste a URL — or choose Upload below"
        style={{ padding: "10px 12px", background: "rgba(0,0,0,.3)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 8, color: "white", fontSize: 13, outline: "none", fontFamily: "var(--font-sans)" }}
      />
      <input
        type="text" value={label} onChange={(e) => setLabel(e.target.value)}
        placeholder="Label (optional) — e.g. 'Reading material'"
        style={{ padding: "10px 12px", background: "rgba(0,0,0,.3)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 8, color: "white", fontSize: 13, outline: "none", fontFamily: "var(--font-sans)" }}
      />
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <label style={{ padding: "8px 14px", background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 999, color: "rgba(255,255,255,.8)", fontSize: 13, cursor: "pointer", fontWeight: 500 }}>
          {uploading ? "Uploading…" : "Upload file"}
          <input
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.csv,.png,.jpg,.jpeg,.webp"
            onChange={(e) => onFile(e.target.files?.[0])}
            style={{ display: "none" }}
          />
        </label>
        <span style={{ flex: 1 }} />
        <button
          onClick={() => { setOpen(false); setUrl(""); setLabel(""); }}
          style={{ padding: "8px 14px", background: "transparent", border: "1px solid rgba(255,255,255,.1)", borderRadius: 999, color: "rgba(255,255,255,.6)", fontSize: 13, cursor: "pointer" }}
        >Cancel</button>
        <button
          onClick={submit}
          disabled={!url.trim()}
          style={{
            padding: "8px 16px",
            background: url.trim() ? "white" : "rgba(255,255,255,.1)",
            border: "none", borderRadius: 999,
            color: url.trim() ? "black" : "rgba(255,255,255,.4)",
            fontSize: 13, fontWeight: 600,
            cursor: url.trim() ? "pointer" : "not-allowed",
          }}
        >Add</button>
      </div>
    </div>
  );
}

/* ═══════════════ Related lesson card (side-rail) ═══════════════
   Shape: horizontal 16:9 thumbnail + compact title + uploader line.
   Same data contract as the Library grid card but much more compact;
   designed to sit in the 340px rail and give at-a-glance context. */
function RelatedItemCard({ item, onOpen }) {
  const inf = inferContent(item.content_url || item.canva_url || "");
  const KindIcon = inf.kind === "video" ? Play : inf.kind === "deck" ? Layers : FileText;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      style={{
        display: "grid",
        gridTemplateColumns: "140px 1fr",
        gap: 10,
        padding: 8,
        borderRadius: 10,
        cursor: "pointer",
        transition: "background .15s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,.04)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
    >
      <div style={{
        aspectRatio: "16/9",
        background: "rgba(255,255,255,.06)",
        borderRadius: 8,
        overflow: "hidden",
        position: "relative",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <KindIcon size={24} strokeWidth={1.5} style={{ color: "rgba(255,255,255,.35)" }} />
        {item.completed_by_me && (
          <div style={{
            position: "absolute", bottom: 4, right: 4,
            background: "rgba(34,197,94,.95)",
            borderRadius: 4,
            padding: "1px 5px",
            fontSize: 9, fontWeight: 700,
            color: "#062d15",
          }}>
            ✓
          </div>
        )}
      </div>
      <div style={{ minWidth: 0, paddingTop: 2 }}>
        <div style={{
          fontSize: 13,
          fontWeight: 500,
          color: "white",
          lineHeight: 1.35,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}>
          {item.title}
        </div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,.45)", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {item.added_by_name || "—"}
        </div>
        <div style={{ fontSize: 10, color: "rgba(255,255,255,.35)", marginTop: 2 }}>
          {inf.label}{item.viewer_count > 0 ? ` · ${item.viewer_count} view${item.viewer_count === 1 ? "" : "s"}` : ""}
        </div>
      </div>
    </div>
  );
}
